import { Joyride, ACTIONS, EVENTS, type Controls, type EventData } from 'react-joyride';
import { useActiveTour, useTour } from '../hooks/useTour';

// Brand-aligned Joyride wrapper. Mounted once in Layout; reacts to whatever
// useTour.start(...) puts in the module-scope state. Joyride itself only
// renders anything when run=true, so the cost when no tour is active is
// effectively zero.

const LOCALE = {
  back: 'Back',
  close: 'Close',
  last: 'Got it',
  next: 'Next',
  skip: 'Skip',
};

export const TourEngine = () => {
  const tour = useActiveTour();
  const { finish, cancel } = useTour();

  const handleEvent = (data: EventData, _controls: Controls) => {
    // Tour ended (last step completed or programmatically stopped).
    if (data.type === EVENTS.TOUR_END) {
      if (tour) void finish(tour);
      return;
    }
    // User clicked Skip or Close.
    if (data.action === ACTIONS.SKIP || data.action === ACTIONS.CLOSE) {
      if (tour) void finish(tour);
      return;
    }
    // A selector didn't resolve. Cleanest fallback is to cancel rather than
    // freeze the tour on a missing step (usually means the user navigated
    // away mid-tour).
    if (data.type === EVENTS.TARGET_NOT_FOUND) {
      cancel();
    }
  };

  if (!tour) return null;

  return (
    <Joyride
      steps={tour.steps}
      run
      continuous
      scrollToFirstStep
      onEvent={handleEvent}
      locale={LOCALE}
      options={{
        primaryColor: '#FF6600',
        textColor: '#1f2937',
        backgroundColor: '#ffffff',
        overlayColor: 'rgba(11, 18, 114, 0.4)',
        arrowColor: '#ffffff',
        showProgress: true,
        skipBeacon: true,
        overlayClickAction: false,
        buttons: ['back', 'skip', 'primary'],
        // Leave room for the sticky header (h-14 = 56px) plus a little
        // breathing space so the highlighted target doesn't sit right
        // under it. If the DeployUpdateBanner is ever visible during a
        // tour this is still tight, but that combination is rare.
        scrollOffset: 80,
      }}
      styles={{
        tooltip: { borderRadius: 12, padding: 16, fontSize: 14 },
        buttonPrimary: {
          borderRadius: 6,
          padding: '8px 14px',
          fontSize: 13,
          fontWeight: 600,
        },
        buttonBack: { color: '#0B1272', marginRight: 8, fontSize: 13 },
        buttonSkip: { color: '#6b7280', fontSize: 13 },
      }}
    />
  );
};
